import React, { Fragment } from "react";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  Box,
  Checkbox,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import DatePicker2 from "../../../../../../../views/components/common/textFields/DatePicker2";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import {
  getModernPageColors,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTableRowSx,
} from "../../../../../../../styles/modernListingPageSx";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";
import InventoryAdjustmentDialogShell from "./InventoryAdjustmentDialogShell";
import AdjustNewSkuLines from "./AdjustNewSkuLines";
import { StockAdjustmentRow } from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import {
  AdjustmentReasonField,
  InlineFormSuccess,
  MobileSelectAllBar,
  MobileStockRowCard,
  useIsCompactLayout,
  SkuStockSummaryTable,
  StockEmptyState,
  StockLoadingState,
} from "./InventoryAdjustmentParts";

interface SwageazyInventoryAdjustPopupProps {
  store: SwageazyInventoryAdjustmentStore;
  onSaved: () => void;
}

/** Fixed so the column doesn't jump when the date picker appears. */
const EXPIRY_COLUMN_SX = { width: 180, minWidth: 180, maxWidth: 180 };

const parsedQty = (raw: string): number | null =>
  /^-?\d+$/.test(raw.trim()) ? Number(raw.trim()) : null;

const SwageazyInventoryAdjustPopup: React.FC<
  SwageazyInventoryAdjustPopupProps
> = ({ store, onSaved }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const headerSx = modernTableHeaderCellSx(theme);
  const cellSx = modernTableBodyCellSx(theme);
  const isCompact = useIsCompactLayout();

  const rows = store.rows;
  const selectedCount = store.selectedRows.length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;

  const toggleAll = (selected: boolean) =>
    store.setRowsSelected(
      rows.map((row) => row.key),
      selected,
    );

  const newQtyColor = (qty: number | null, newQty: number | null) => {
    if (newQty === null || qty === 0) return colors.title;
    if (newQty < 0) return colors.error;
    return (qty ?? 0) > 0 ? colors.success : colors.debit;
  };

  const formatExistingExpiry = (row: StockAdjustmentRow) =>
    row.existing_expiry_date && dayjs(row.existing_expiry_date).isValid()
      ? dayjs(row.existing_expiry_date).format("DD MMM YYYY")
      : "—";

  const renderQtyField = (row: StockAdjustmentRow, hasError: boolean) => (
    <CustomTextField
      type="number"
      allowNegativeValue
      allowDecimalValue={false}
      placeholder="±0"
      width="100%"
      value={row.quantity}
      error={hasError}
      disabled={store.isSubmitting}
      onChange={(e) =>
        store.updateRow(row.key, {
          quantity: e.target.value,
          selected: e.target.value.trim() !== "" ? true : row.selected,
        })
      }
    />
  );

  const renderExpiryField = (row: StockAdjustmentRow) => (
    <DatePicker2
      type="date"
      label="Expiry"
      value={row.expiry_date ? dayjs(row.expiry_date) : null}
      minDate={dayjs()}
      onChange={(value) =>
        store.updateRow(row.key, {
          expiry_date: value ? value.format("YYYY-MM-DD") : undefined,
        })
      }
      borderRadius="8px"
      zIndex={4000}
      disabled={store.isSubmitting}
    />
  );

  const renderNoteField = (row: StockAdjustmentRow) => (
    <CustomTextField
      placeholder="Note (optional)"
      width="100%"
      value={row.note}
      disabled={store.isSubmitting}
      onChange={(e) => store.updateRow(row.key, { note: e.target.value })}
    />
  );

  const handleSave = async () => {
    try {
      const result = await store.submitAdjustment();
      if (result) onSaved();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <InventoryAdjustmentDialogShell
      open={store.isAdjustOpen}
      title="Adjust Stock"
      subtitle={
        store.product ? `#${store.product.id} · ${store.product.name}` : ""
      }
      onClose={store.closeAdjust}
      disableClose={store.isSubmitting}
      footer={
        <>
          <AppButton
            variant="transparentPrimary"
            label="Close"
            onClick={store.closeAdjust}
            disabled={store.isSubmitting}
          />
          <AppButton
            variant="primary"
            label={
              store.isSubmitting
                ? "Saving..."
                : `Save Adjustment${store.adjustLineCount ? ` (${store.adjustLineCount})` : ""}`
            }
            onClick={handleSave}
            disabled={store.isSubmitting || store.isStockLoading}
          />
        </>
      }
    >
      {store.isStockLoading && rows.length === 0 ? (
        <StockLoadingState />
      ) : store.stockError ? (
        <InlineFormError message={store.stockError} />
      ) : (
        <>
          {rows.length === 0 ? (
            <StockEmptyState message="No stock yet. Use Add SKU to add stock." />
          ) : (
            <>
              <SkuStockSummaryTable summaries={store.summaries} />

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <CustomTypography variant="heading7" color={colors.title}>
                    Adjust Boxes
                  </CustomTypography>
                  <CustomTypography variant="caption" color={colors.subtitle}>
                    Positive adds stock, negative removes it (e.g. 5 or -3).
                  </CustomTypography>
                </Box>
                {isCompact ? (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <MobileSelectAllBar
                      total={rows.length}
                      selectedCount={selectedCount}
                      onToggle={toggleAll}
                      disabled={store.isSubmitting}
                    />
                    {rows.map((row) => {
                      const qty = parsedQty(row.quantity);
                      const newQty = qty === null ? null : row.on_hand + qty;
                      const rowError = store.rowErrors[row.key];
                      return (
                        <MobileStockRowCard
                          key={row.key}
                          sku={row.sku}
                          selected={row.selected}
                          onToggle={(selected) =>
                            store.setRowsSelected([row.key], selected)
                          }
                          disabled={store.isSubmitting}
                          error={rowError}
                          meta={[
                            { label: "Warehouse", value: row.warehouse_id },
                            { label: "Box", value: row.box_id },
                            { label: "Rack", value: row.rack || "—" },
                            { label: "In Box", value: row.on_hand },
                            {
                              label: "New Qty",
                              value: (
                                <Box
                                  component="span"
                                  sx={{ color: newQtyColor(qty, newQty) }}
                                >
                                  {newQty === null ? "—" : newQty}
                                </Box>
                              ),
                            },
                            {
                              label: "Current Expiry",
                              value: formatExistingExpiry(row),
                            },
                          ]}
                        >
                          <Grid2 size={{ xs: 12, sm: 4 }}>
                            {renderQtyField(row, Boolean(rowError))}
                          </Grid2>
                          {(qty ?? 0) > 0 && (
                            <Grid2 size={{ xs: 12, sm: 4 }}>
                              {renderExpiryField(row)}
                            </Grid2>
                          )}
                          <Grid2 size={{ xs: 12, sm: (qty ?? 0) > 0 ? 4 : 8 }}>
                            {renderNoteField(row)}
                          </Grid2>
                        </MobileStockRowCard>
                      );
                    })}
                  </Box>
                ) : (
                  <TableContainer
                    sx={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: 2,
                      maxHeight: "45vh",
                      overflow: "auto",
                    }}
                  >
                    <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ ...headerSx, width: 48 }}>
                            <Checkbox
                              size="small"
                              checked={allSelected}
                              indeterminate={selectedCount > 0 && !allSelected}
                              onChange={(e) => toggleAll(e.target.checked)}
                              disabled={store.isSubmitting}
                            />
                          </TableCell>
                          <TableCell sx={headerSx}>SKU</TableCell>
                          <TableCell sx={headerSx}>Warehouse</TableCell>
                          <TableCell sx={headerSx}>Box</TableCell>
                          <TableCell sx={headerSx}>Rack</TableCell>
                          <TableCell sx={headerSx}>In Box</TableCell>
                          <TableCell sx={{ ...headerSx, minWidth: 110 }}>
                            Adjust Qty
                          </TableCell>
                          <TableCell sx={headerSx}>New Qty</TableCell>
                          <TableCell sx={{ ...headerSx, ...EXPIRY_COLUMN_SX }}>
                            Expiry (when adding)
                          </TableCell>
                          <TableCell sx={{ ...headerSx, minWidth: 180 }}>
                            Note
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((row) => {
                          const qty = parsedQty(row.quantity);
                          const newQty =
                            qty === null ? null : row.on_hand + qty;
                          const rowError = store.rowErrors[row.key];
                          return (
                            <Fragment key={row.key}>
                              <TableRow
                                sx={{
                                  ...modernTableRowSx(theme),
                                  ...(row.selected && {
                                    bgcolor: colors.surfaceSoft,
                                  }),
                                }}
                              >
                                <TableCell sx={cellSx}>
                                  <Checkbox
                                    size="small"
                                    checked={row.selected}
                                    onChange={(e) =>
                                      store.setRowsSelected(
                                        [row.key],
                                        e.target.checked,
                                      )
                                    }
                                    disabled={store.isSubmitting}
                                  />
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  <SkuCopyRow
                                    sku={row.sku}
                                    color={colors.title}
                                    fontWeight={600}
                                    variant="body3"
                                  />
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {row.warehouse_id}
                                </TableCell>
                                <TableCell sx={cellSx}>{row.box_id}</TableCell>
                                <TableCell sx={cellSx}>
                                  {row.rack || "—"}
                                </TableCell>
                                <TableCell sx={{ ...cellSx, fontWeight: 600 }}>
                                  {row.on_hand}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {renderQtyField(row, Boolean(rowError))}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellSx,
                                    fontWeight: 700,
                                    color: newQtyColor(qty, newQty),
                                  }}
                                >
                                  {newQty === null ? "—" : newQty}
                                </TableCell>
                                <TableCell
                                  sx={{ ...cellSx, ...EXPIRY_COLUMN_SX }}
                                >
                                  {(qty ?? 0) > 0 ? (
                                    renderExpiryField(row)
                                  ) : (
                                    <CustomTypography
                                      variant="body3"
                                      color={colors.subtitle}
                                    >
                                      {formatExistingExpiry(row)}
                                    </CustomTypography>
                                  )}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  {renderNoteField(row)}
                                </TableCell>
                              </TableRow>
                              {rowError && (
                                <TableRow>
                                  <TableCell
                                    colSpan={10}
                                    sx={{ py: 0, border: 0 }}
                                  >
                                    <InlineFormError message={rowError} />
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </>
          )}

          <AdjustNewSkuLines store={store} />

          <AdjustmentReasonField
            value={store.reason}
            onChange={store.setReason}
            disabled={store.isSubmitting}
          />
        </>
      )}

      <InlineFormError message={store.formError} sx={{ mt: 0, mb: 0 }} />
      <InlineFormSuccess message={store.successMessage} />
    </InventoryAdjustmentDialogShell>
  );
};

export default observer(SwageazyInventoryAdjustPopup);
